import { useEffect, useState } from 'react';
import { Skull, X, Check, Loader2, AlertTriangle } from 'lucide-react';
import { getMexcDisclaimerStatus, acknowledgeMexcDisclaimer } from '../services/api';

/**
 * One-time disclaimer required before user can connect MEXC API keys or route
 * orders to MEXC. Backend enforces this server-side too — frontend modal is UX.
 *
 * Usage:
 *   const { needed, loading, acknowledge } = useMexcDisclaimer();
 *   if (needed) <MexcDisclaimerModal onConfirmed={...} onCancel={...} />
 */

export function useMexcDisclaimer() {
  const [needed, setNeeded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disclaimerText, setDisclaimerText] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await getMexcDisclaimerStatus();
      const data = res?.data || res;
      setNeeded(!data.acknowledged || data.requiresReAck);
      setDisclaimerText(data.disclaimerText || '');
    } catch (err) {
      console.error('Failed to load MEXC disclaimer status', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return { needed, loading, disclaimerText, refresh };
}

export default function MexcDisclaimerModal({ onConfirmed, onCancel }) {
  const { loading, disclaimerText } = useMexcDisclaimer();
  const [confirmed1, setConfirmed1] = useState(false);
  const [confirmed2, setConfirmed2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleConfirm() {
    if (!confirmed1 || !confirmed2) return;
    setSubmitting(true);
    setError(null);
    try {
      await acknowledgeMexcDisclaimer();
      if (onConfirmed) onConfirmed();
    } catch (err) {
      setError(err.message || 'Acknowledgment failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0d0e14] border border-violet-500/40 rounded-2xl max-w-2xl w-full my-8 max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-dark-700/50 bg-violet-500/5 rounded-t-2xl">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                <Skull className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-violet-400 mb-0.5">High-Leverage Exchange</p>
                <h2 className="text-xl font-bold text-white">MEXC Risk Acknowledgment</h2>
              </div>
            </div>
            <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-gray-400">
            Required reading before haythix can route orders to MEXC. One-time acknowledgment.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading disclaimer…
            </div>
          ) : (
            <>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 text-sm font-bold mb-1">Sharp tool warning</p>
                  <p className="text-xs text-gray-300">
                    MEXC offers leverage up to <span className="text-violet-400 font-bold">500x</span> on select pairs.
                    Above 50x, industry data shows that more than 95% of users liquidate within minutes during normal volatility.
                  </p>
                </div>
              </div>

              <div className="bg-dark-700/30 border border-dark-600/40 rounded-lg p-4">
                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">{disclaimerText}</pre>
              </div>

              {/* Two checkboxes — defense in depth */}
              <label className="flex items-start gap-3 p-3 bg-dark-700/30 border border-dark-600/40 rounded-lg cursor-pointer hover:border-violet-500/40">
                <input type="checkbox" checked={confirmed1} onChange={e => setConfirmed1(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-dark-500" />
                <span className="text-xs text-gray-300">
                  I understand that <span className="text-white font-medium">haythix does not set, endorse, or guarantee any leverage level</span> offered by MEXC, and that all trading risk is borne by me.
                </span>
              </label>

              <label className="flex items-start gap-3 p-3 bg-dark-700/30 border border-dark-600/40 rounded-lg cursor-pointer hover:border-violet-500/40">
                <input type="checkbox" checked={confirmed2} onChange={e => setConfirmed2(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-dark-500" />
                <span className="text-xs text-gray-300">
                  I confirm I am responsible for complying with my local laws regarding crypto derivatives trading, and accept that haythix is not liable for losses, liquidations, or fees on my MEXC positions.
                </span>
              </label>

              {error && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs">{error}</div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-dark-700/50 flex gap-3">
          <button onClick={onCancel} className="flex-1 text-sm py-2.5 rounded-xl border border-dark-600 text-gray-300 hover:bg-dark-700">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={!confirmed1 || !confirmed2 || submitting}
            className="flex-1 text-sm font-semibold py-2.5 rounded-xl bg-violet-500 text-white hover:bg-violet-400 disabled:bg-dark-600 disabled:text-gray-500 flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Check className="w-4 h-4" /> I Understand the Risk</>}
          </button>
        </div>
      </div>
    </div>
  );
}
